export const TAB_LINKED = {
  init() {
    TabsLinked.initAll('[data-tabs][data-tabs-ex="linked"]')
    TabsLinked.bindRemote()
    TabsLinked.bindHash()
    TabsLinked.applyHashOnLoad()
  },
}

class TabsLinked {
  static initAll(selector) {
    const roots = document.querySelectorAll(selector)

    Array.from(roots).forEach(root => {
      if (root.__tabsLinkedInstance) return
      root.__tabsLinkedInstance = new TabsLinked(root)
      root.__tabsLinkedInstance.init()
    })
  }

  static bindRemote() {
    if (TabsLinked._remoteBound) return
    TabsLinked._remoteBound = true

    document.addEventListener('click', e => {
      const link = e.target.closest('.js-tabs-remote a[href^="#"]')
      if (!link) return

      const id = TabsLinked.getHashId(link.getAttribute('href'))
      if (!id) return

      const panel = document.getElementById(id)
      if (!panel) return

      const root = panel.closest('[data-tabs][data-tabs-ex="linked"]')
      if (!root || !root.__tabsLinkedInstance) return

      e.preventDefault()

      root.__tabsLinkedInstance.activateByPanelId(id, {
        focus: false,
        updateHash: true,
        skipAnim: false,
      })
    })
  }

  static bindHash() {
    if (TabsLinked._hashBound) return
    TabsLinked._hashBound = true

    window.addEventListener('hashchange', () => {
      TabsLinked.applyHash()
    })
  }

  static applyHashOnLoad() {
    TabsLinked.applyHash()
  }

  static applyHash() {
    const id = TabsLinked.getHashId(window.location.hash)
    if (!id) return

    const panel = document.getElementById(id)
    if (!panel) return

    const root = panel.closest('[data-tabs][data-tabs-ex="linked"]')
    if (!root || !root.__tabsLinkedInstance) return

    root.__tabsLinkedInstance.activateByPanelId(id, {
      focus: false,
      updateHash: false,
      skipAnim: false,
    })
  }

  static getHashId(hash) {
    if (!hash) return ''
    const s = String(hash).trim()
    if (!s) return ''
    return s.charAt(0) === '#' ? s.slice(1) : s
  }

  static setHashNoJump(id) {
    const next = `#${id}`
    if (window.location.hash === next) return

    if (window.history && window.history.pushState) {
      window.history.pushState(null, '', next)
      return
    }

    window.location.hash = next
  }

  constructor(root) {
    this.root = root

    this.fadeMs = this.getFadeMs()
    this._fadeTid = 0
    this._rafIn = 0
    this._pendingHide = null

    this.onClick = this.onClick.bind(this)
    this.onKeydown = this.onKeydown.bind(this)
  }

  init() {
    this.tabLists = Array.from(this.root.querySelectorAll('[role="tablist"]'))
    this.tabs = Array.from(this.root.querySelectorAll('[role="tab"]'))
    this.panels = Array.from(this.root.querySelectorAll('[role="tabpanel"]'))

    if (!this.tabs.length || !this.panels.length) return

    this.activation =
      (this.root.getAttribute('data-activation') || 'auto').toLowerCase() === 'manual'
        ? 'manual'
        : 'auto'

    this.root.addEventListener('click', this.onClick)
    this.root.addEventListener('keydown', this.onKeydown)

    const initial =
      this.tabs.find(t => t.classList.contains('is-active')) ||
      this.tabs.find(t => t.getAttribute('aria-selected') === 'true') ||
      this.tabs[0]

    this.activateTab(initial, { focus: false, updateHash: false, skipAnim: true })
  }

  prefersReducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches)
  }

  getFadeMs() {
    const v = this.root.getAttribute('data-fade')
    const n = parseInt(v, 10)
    if (!Number.isNaN(n)) return n
    return 300
  }

  cancelPending() {
    if (this._fadeTid) {
      clearTimeout(this._fadeTid)
      this._fadeTid = 0
    }
    if (this._rafIn) {
      cancelAnimationFrame(this._rafIn)
      this._rafIn = 0
    }
    if (this._pendingHide) {
      this._pendingHide.classList.remove('is-fading-out')
      this._pendingHide = null
    }
  }

  getCurrentPanel() {
    for (let i = 0; i < this.panels.length; i += 1) {
      const p = this.panels[i]
      if (p.getAttribute('aria-hidden') === 'false' && !p.hasAttribute('hidden')) return p
    }
    return null
  }

  getPanelIdFromTab(tab) {
    const raw = (tab.getAttribute('aria-controls') || '').trim()
    if (!raw) return ''
    return raw.charAt(0) === '#' ? raw.slice(1) : raw
  }

  getTabByPanelId(panelId) {
    const id = String(panelId || '').trim()
    if (!id) return null
    return this.tabs.find(t => this.getPanelIdFromTab(t) === id) || null
  }

  getTabsInList(listEl) {
    if (!listEl) return this.tabs
    return Array.from(listEl.querySelectorAll('[role="tab"]'))
  }

  activateByPanelId(panelId, opts) {
    const tab = this.getTabByPanelId(panelId)
    if (!tab) return
    this.activateTab(tab, opts)
  }

  activateTab(tab, opts) {
    const opt = opts || {}
    const focus = !!opt.focus
    const updateHash = !!opt.updateHash
    const skipAnim = !!opt.skipAnim

    if (!tab || this.tabs.indexOf(tab) === -1) return

    const panelId = this.getPanelIdFromTab(tab)
    const nextPanel = panelId ? document.getElementById(panelId) : null
    if (!nextPanel) return

    this.cancelPending()

    const currentPanel = this.getCurrentPanel()

    const doAnim = !skipAnim && !this.prefersReducedMotion() && this.fadeMs > 0

    // tabs
    this.tabs.forEach(t => {
      const isActive = t === tab
      t.classList.toggle('is-active', isActive)
      t.setAttribute('aria-selected', isActive ? 'true' : 'false')
    })

    // tablist分割：各tablistに1つ tabindex=0
    const lists = this.tabLists.length ? this.tabLists : [null]
    lists.forEach(listEl => {
      const inList = this.getTabsInList(listEl)
      if (!inList.length) return

      const focusable = inList.indexOf(tab) !== -1 ? tab : inList[0]
      inList.forEach(t => t.setAttribute('tabindex', t === focusable ? '0' : '-1'))
    })

    // show next (hidden→復帰でも必ずフェードイン)
    nextPanel.classList.remove('is-fading-out')
    nextPanel.removeAttribute('hidden')
    nextPanel.setAttribute('aria-hidden', 'false')

    if (doAnim) {
      nextPanel.classList.add('is-active')
      nextPanel.classList.add('is-fading-in')

      this._rafIn = requestAnimationFrame(() => {
        this._rafIn = 0
        nextPanel.classList.remove('is-fading-in')
      })
    } else {
      nextPanel.classList.add('is-active')
      nextPanel.classList.remove('is-fading-in')
    }

    // hide current (fade-out → 後でhidden)
    if (currentPanel && currentPanel !== nextPanel) {
      if (doAnim) {
        currentPanel.classList.add('is-fading-out')
        currentPanel.classList.remove('is-active')

        this._pendingHide = currentPanel

        this._fadeTid = setTimeout(() => {
          if (!this._pendingHide) return
          this._pendingHide.setAttribute('hidden', '')
          this._pendingHide.setAttribute('aria-hidden', 'true')
          this._pendingHide.classList.remove('is-fading-out')
          this._pendingHide = null
          this._fadeTid = 0
        }, this.fadeMs + 40)
      } else {
        currentPanel.setAttribute('hidden', '')
        currentPanel.setAttribute('aria-hidden', 'true')
        currentPanel.classList.remove('is-fading-out')
        currentPanel.classList.remove('is-active')
      }
    }

    // other panels
    this.panels.forEach(p => {
      if (p === nextPanel) return
      if (doAnim && p === this._pendingHide) return

      p.setAttribute('hidden', '')
      p.setAttribute('aria-hidden', 'true')
      p.classList.remove('is-active')
      p.classList.remove('is-fading-in')
      if (!doAnim || p !== this._pendingHide) p.classList.remove('is-fading-out')
    })

    // 外部トリガー同期
    this.syncRemoteTriggers(panelId)

    // hash同期（ジャンプなし）
    if (updateHash && panelId) TabsLinked.setHashNoJump(panelId)

    if (focus) tab.focus()
  }

  syncRemoteTriggers(activePanelId) {
    const links = document.querySelectorAll('.js-tabs-remote a[href^="#"]')

    Array.from(links).forEach(a => {
      const id = TabsLinked.getHashId(a.getAttribute('href'))
      if (!id) return

      const panel = document.getElementById(id)
      if (!panel) return

      const root = panel.closest('[data-tabs][data-tabs-ex="linked"]')
      if (root !== this.root) return

      const li = a.closest('.js-tabs-remote')
      const isActive = id === activePanelId

      if (li) li.classList.toggle('is-active', isActive)
      if (isActive) a.setAttribute('aria-current', 'true')
      else a.removeAttribute('aria-current')
    })
  }

  onClick(e) {
    const tab = e.target.closest('[role="tab"]')
    if (!tab || !this.root.contains(tab)) return

    e.preventDefault()
    this.activateTab(tab, { focus: true, updateHash: true, skipAnim: false })
  }

  onKeydown(e) {
    const tab = e.target.closest('[role="tab"]')
    if (!tab || !this.root.contains(tab)) return

    const key = e.key

    const tabList = tab.closest('[role="tablist"]')
    const tabsInList = tabList ? this.getTabsInList(tabList) : this.tabs
    const idx = tabsInList.indexOf(tab)
    if (idx < 0) return

    const move = nextIdx => {
      const next = tabsInList[nextIdx]
      if (!next) return

      if (this.activation === 'auto') {
        this.activateTab(next, { focus: true, updateHash: true, skipAnim: false })
      } else {
        next.focus()
      }
    }

    if (key === 'ArrowRight' || key === 'ArrowDown') {
      e.preventDefault()
      move((idx + 1) % tabsInList.length)
      return
    }

    if (key === 'ArrowLeft' || key === 'ArrowUp') {
      e.preventDefault()
      move((idx - 1 + tabsInList.length) % tabsInList.length)
      return
    }

    if (key === 'Home') {
      e.preventDefault()
      move(0)
      return
    }

    if (key === 'End') {
      e.preventDefault()
      move(tabsInList.length - 1)
      return
    }

    if (this.activation === 'manual' && (key === 'Enter' || key === ' ')) {
      e.preventDefault()
      this.activateTab(tab, { focus: true, updateHash: true, skipAnim: false })
    }
  }
}

TabsLinked._remoteBound = false
TabsLinked._hashBound = false
